import React, { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Callback, CallbackEidas, AuthRouterProvider, ProtectedRoute } from "./features/auth";
import { ConsentPage as ConsentPopup } from "./features/consent";
import { LoginPage as EvidenceSubmission } from "./features/login";
import { PreviewSpacePage as DocumentSubmission } from "./features/preview";
import { Identification } from "./features/identification";
import { SessionExpiredNotice, ForceLogoutModal, useSessionTimer, storeSessionParams, logoutSilent } from "./features/session";
import ErrorPage from "./shared/pages/ErrorPage";
import { logger } from "./shared/utils/logger.client";
import AppLayout from "./shared/layouts/AppLayout/AppLayout";
import BareLayout from "./shared/layouts/BareLayout/BareLayout";
import Identification2 from "./features/identification2/Identification2";
import './styles/app-prime.scss';
import './styles/variables.scss';


const HIDE_ON = new Set<string>([
  "/",
  "/login",
  "/preview/callback",
  "/preview/eidas-callback",
  "/information",
  "/error",
]);

function GlobalSessionNotice() {
  const { pathname } = useLocation();
  const { getRemainingTime, timeLeft } = useSessionTimer();

  const norm = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const hasStart = !!localStorage.getItem("requestStartTime");

  if (HIDE_ON.has(norm)) return null;
  if (!hasStart && timeLeft <= 0) return null;

  return <SessionExpiredNotice warnBeforeMs={10_000} />;
}

const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const { disableAllSessionTimeouts } = useSessionTimer();

  // Store session params once on app boot (PS only)
  useEffect(() => {
    storeSessionParams();
  }, []);

  // ✅ Guard logout during OIDC redirects/callbacks
  const shouldSkipLogout = () => {
    const isRedirecting = sessionStorage.getItem("oidcRedirect") === "1";
    const isCompleting = sessionStorage.getItem("authCompleting") === "1";
    return isRedirecting || isCompleting;
  };

  useEffect(() => {
    const isOidcFlow = () =>
      sessionStorage.getItem("oidcRedirect") === "1" ||
      sessionStorage.getItem("authCompleting") === "1";

    const handleBeforeUnload = () => {
      if (isOidcFlow()) {
        logger.debug("🚪 beforeunload during OIDC redirect/callback — NOT logging out");
        return;
      }
      logger.debug("🚪 beforeunload — disabling session timer + silent logout");
      disableAllSessionTimeouts();
      void logoutSilent(); // IMPORTANT: don’t navigate here
    };

    const handlePageHide = () => {
      if (isOidcFlow()) {
        logger.debug("🚪 pagehide during OIDC redirect/callback — NOT logging out");
        return;
      }
      logger.debug("🚪 pagehide — disabling session timer + silent logout");
      disableAllSessionTimeouts();
      void logoutSilent(); // IMPORTANT: don’t navigate here
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [disableAllSessionTimeouts]);

  // ENV-based log level
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/env");
        const env = await res.json();
        const nodeEnv = env.NODE_ENV;
        const level = nodeEnv === "production" ? "warn" : "debug";
        logger.info(`[ENV] Loaded NODE_ENV=${nodeEnv}`);
      } catch (e) {
        logger.error("Failed to fetch /env:", e);
      }
    })();
  }, []);

  return (
    <div className="App">
      <GlobalSessionNotice />
      <ForceLogoutModal />

      <Routes>
        {/* BareLayout (no logout header) */}
        <Route element={<BareLayout />}>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/preview/callback" element={<Callback />} />
          <Route path="/preview/eidas-callback" element={<CallbackEidas />} />
        </Route>

        {/* AppLayout (with logout header, no protection) */}
        <Route element={<AppLayout />}>
          <Route path="/login" element={<EvidenceSubmission />} />
          <Route path="/information" element={<Identification />} />
          <Route path="/error" element={<ErrorPage />} />
        </Route>

        {/* AppLayout + ProtectedRoute (with logout + auth required) */}
        <Route element={<AppLayout />}>
          <Route element={<ProtectedRoute />}>
            <Route path="/consent-legacy" element={<ConsentPopup />} />
            <Route path="/preview" element={<Identification2 />} />
            <Route path="/previewspace" element={<DocumentSubmission />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthRouterProvider>
        <AppShell />
      </AuthRouterProvider>
    </Router>
  );
}

export default App;
